import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, RefreshCw, Send, Mail, AlertTriangle, Eye, Code2 } from "lucide-react";
import { useGenerateNewsletter, type NewsletterPreview } from "@/hooks/useNewsletter";
import { toast } from "sonner";

const NEWSLETTER_SEND_ENABLED = false;

export default function Newsletter() {
  const generate = useGenerateNewsletter();
  const [preview, setPreview] = useState<NewsletterPreview | null>(null);
  const [editedIntro, setEditedIntro] = useState<string>("");
  const [view, setView] = useState<"rendered" | "html">("rendered");

  useEffect(() => {
    generate.mutate(
      {},
      {
        onSuccess: (data) => {
          setPreview(data);
          setEditedIntro(data.intro_html);
        },
        onError: (e: unknown) => {
          const msg = e instanceof Error ? e.message : "Generation failed";
          toast.error(msg);
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch; generate.mutate is stable
  }, []);

  const handleRegenerate = () => {
    generate.mutate(
      { regenerate_intro: true },
      {
        onSuccess: (data) => {
          setPreview(data);
          setEditedIntro(data.intro_html);
          toast.success("Newsletter regenerated");
        },
        onError: (e: unknown) => {
          const msg = e instanceof Error ? e.message : "Generation failed";
          toast.error(msg);
        },
      },
    );
  };

  const previewHtml = preview
    ? preview.html.replace(preview.intro_html, editedIntro)
    : "";

  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Mail className="h-6 w-6 text-primary" />
            Weekly Newsletter
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Preview-only assembler. Pulls top 4–6 news intelligence rows from the last 7 days
            with a Sonnet-drafted intro Jake will edit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRegenerate}
            disabled={generate.isPending}
          >
            {generate.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Regenerate
          </Button>
          <Button
            size="sm"
            disabled={!NEWSLETTER_SEND_ENABLED}
            title={
              NEWSLETTER_SEND_ENABLED
                ? "Send to ELT"
                : "Send disabled — awaiting orchestrator approval"
            }
          >
            <Send className="h-4 w-4 mr-2" />
            Send to ELT
          </Button>
        </div>
      </div>

      {!NEWSLETTER_SEND_ENABLED && (
        <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 mb-4 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            <strong>Preview only.</strong> Send button is disabled. First runs are manual; cron
            is not enabled. Brian decides delivery.
          </div>
        </div>
      )}

      {generate.isPending && !preview && (
        <Card className="p-12 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mr-3" />
          <span className="text-sm text-muted-foreground">Generating preview…</span>
        </Card>
      )}

      {preview && preview.articles.length === 0 && (
        <Card className="p-6">
          <div className="text-sm text-muted-foreground">
            No articles passed the relevance threshold in the last 7 days.
          </div>
        </Card>
      )}

      {preview && preview.articles.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left rail — editable intro + article list */}
          <div className="space-y-4 lg:col-span-1">
            <Card className="p-4">
              <Label className="text-sm font-medium mb-2 block">
                Editorial intro (HTML — edit before sending)
              </Label>
              <Textarea
                value={editedIntro}
                onChange={(e) => setEditedIntro(e.target.value)}
                className="min-h-[200px] font-mono text-xs"
                spellCheck={false}
              />
              <div className="text-[11px] text-muted-foreground mt-2">
                Edits are local — they update the rendered preview live. The send pipeline does
                not exist yet, so changes are not persisted.
              </div>
            </Card>

            <Card className="p-4">
              <div className="text-sm font-medium mb-2">
                Articles selected ({preview.articles.length})
              </div>
              <ul className="space-y-2">
                {preview.articles.map((a) => (
                  <li key={a.id} className="text-xs border-l-2 border-primary/40 pl-2 py-1">
                    <div className="font-medium text-foreground line-clamp-2">{a.title}</div>
                    <div className="text-muted-foreground mt-0.5">
                      {a.published_date
                        ? new Date(a.published_date).toLocaleDateString()
                        : ""}
                      {a.relevance_score !== null && ` · score ${a.relevance_score.toFixed(2)}`}
                      {(a.prospect_company_name ||
                        (a.matched_accounts && a.matched_accounts[0])) &&
                        ` · ${
                          a.prospect_company_name ||
                          (a.matched_accounts && a.matched_accounts[0])
                        }`}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {/* Right rail — rendered or HTML view */}
          <div className="lg:col-span-2">
            <Card className="p-0 overflow-hidden">
              <div className="border-b px-3 py-2 flex items-center gap-1 bg-muted/30">
                <Button
                  variant={view === "rendered" ? "default" : "ghost"}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setView("rendered")}
                >
                  <Eye className="h-3.5 w-3.5 mr-1" />
                  Rendered
                </Button>
                <Button
                  variant={view === "html" ? "default" : "ghost"}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setView("html")}
                >
                  <Code2 className="h-3.5 w-3.5 mr-1" />
                  HTML
                </Button>
              </div>
              {view === "rendered" ? (
                <iframe
                  srcDoc={previewHtml}
                  className="w-full"
                  style={{ minHeight: "70vh", border: 0 }}
                  title="Newsletter preview"
                  sandbox=""
                />
              ) : (
                <pre className="p-4 text-[11px] font-mono overflow-auto max-h-[70vh] whitespace-pre-wrap">
                  {previewHtml}
                </pre>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
